import React from 'react';

import { Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
// @ts-expect-error TS(2307): Cannot find module 'virtual:pwa-register/react' or... Remove this comment to see the full error message
// eslint-disable-next-line import/no-unresolved
import { useRegisterSW } from 'virtual:pwa-register/react';
import { AccessToAcceleratorsPage } from './components/AccessToAccelerators/AccessToAcceleratorsPage';
import { AddToHomescreen } from './components/AddToHomescreen/AddToHomescreen';
import { AuthWrapper } from './components/AuthWrapper/AuthWrapper';
import { EndpointsDataProvider } from './components/EndpointsData/EndpointsDataProvider';
import { EnvironmentProvider } from './components/EnvironmentProvider/EnvironmentProvider';
import { Header } from './components/Header/Header';
import { ListAccessPoints } from './components/ListAccessPoints/ListAccessPoints';
import { HomeDataProvider } from './components/LoadHomeData/HomeDataProvider';
import { NavigationProvider } from './components/NavigationProvider/NavigationProvider';
import { Notifications } from './components/Notifications/Notifications';
import { Theme } from './components/Theme/Theme';
import { ToastProvider } from './components/ToastProvider/ToastProvider';
import { TranslationsProvider } from './components/Translations/TranslationsProvider';
import { AccessEmulation } from './pages/AccessEmulation';
import { AccessPointPage } from './pages/AccessPointPage';
import { AnonymousVisitorPage } from './pages/AnonymousVisitorPage';
import { CheckAccessPage } from './pages/CheckAccessPage';
import { DevelopmentHomepage } from './pages/DevelopmentHomepage';
import { Error404 } from './pages/Error404';
import { EvacuationPage } from './pages/EvacuationPage';
import { EvacuationsPage } from './pages/EvacuationsPage';
import { FavouritesPage } from './pages/FavouritesPage';
import { HelpPage } from './pages/HelpPage';
import { Homepage } from './pages/Homepage';
import { MyDataPage } from './pages/MyDataPage';
import { NFCPage } from './pages/NFCPage';
import { NotificationsLandingPage } from './pages/NotificationsLandingPage';
import { NotificationsTestPage } from './pages/NotificationsTestPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { RequestAccessPage } from './pages/RequestAccessPage';
import { ScanPage } from './pages/ScanPage';
import { TemporaryBadgePage } from './pages/TemporaryBadgePage';
import { TemporaryBadgeScanResultPage } from './pages/TemporaryBadgeScanResultPage';
import { ValidateQRPage } from './pages/ValidateQRPage';
import { VisitorCardScanResultPage } from './pages/VisitorCardScanResultPage';
import { ROUTES } from './routes';
import { isProductionEnvironment } from './utils/utils';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            cacheTime: 0,
            refetchOnWindowFocus: false,
        },
    },
});

const router = createBrowserRouter([
    {
        element: (
            <QueryClientProvider client={queryClient}>
                <NavigationProvider>
                    <AuthWrapper>
                        <ToastProvider>
                            <HomeDataProvider>
                                <TranslationsProvider>
                                    <EndpointsDataProvider>
                                        <EnvironmentProvider>
                                            {!isProductionEnvironment() && <Notifications />}
                                            <Theme>
                                                <Header />
                                                <AddToHomescreen />
                                                <Box m={2} mt={9}>
                                                    <Outlet />
                                                </Box>
                                            </Theme>
                                        </EnvironmentProvider>
                                    </EndpointsDataProvider>
                                </TranslationsProvider>
                            </HomeDataProvider>
                        </ToastProvider>
                    </AuthWrapper>
                </NavigationProvider>
            </QueryClientProvider>
        ),
        children: [
            { path: ROUTES.homepage, element: <Homepage /> },
            { path: ROUTES.checkaccess, element: <CheckAccessPage /> },
            { path: ROUTES.accesspoint.search, element: <ListAccessPoints /> },
            { path: ROUTES.accesspoint.view, element: <AccessPointPage /> },
            { path: ROUTES.accesspoint.permissions, element: <PermissionsPage /> },
            { path: ROUTES.accesspoint.permits, element: <PermissionsPage /> },
            { path: ROUTES.accesspoint.request, element: <RequestAccessPage /> },
            { path: ROUTES.scan, element: <ScanPage /> },
            { path: ROUTES.temporaryBadge.view, element: <TemporaryBadgePage /> },
            { path: ROUTES.temporaryBadge.result, element: <TemporaryBadgeScanResultPage /> },
            { path: ROUTES.nfc, element: <NFCPage /> },
            { path: ROUTES.favourites, element: <FavouritesPage /> },
            { path: ROUTES.accessEmulation, element: <AccessEmulation /> },
            { path: ROUTES.validateQr, element: <ValidateQRPage /> },
            { path: ROUTES.visitorCard, element: <VisitorCardScanResultPage /> },
            { path: ROUTES.myData, element: <MyDataPage /> },
            { path: ROUTES.help, element: <HelpPage /> },
            { path: ROUTES.notfound, element: <Error404 /> },
            { path: ROUTES.evacuations.search, element: <EvacuationsPage /> },
            { path: ROUTES.evacuations.view, element: <EvacuationPage /> },
            { path: ROUTES.accessToAccelerators, element: <AccessToAcceleratorsPage /> },
            ...(isProductionEnvironment()
                ? []
                : [
                      { path: ROUTES.notifications.search, element: <NotificationsLandingPage /> },
                      { path: ROUTES.notifications.test, element: <NotificationsTestPage /> },
                      { path: ROUTES.development, element: <DevelopmentHomepage /> },
                      { path: ROUTES.anonymous, element: <AnonymousVisitorPage /> },
                  ]),
        ],
    },
]);

export const App = () => {
    useRegisterSW({
        immediate: true,
        onRegisteredSW: (swUrl: string, r: ServiceWorkerRegistration | undefined) => {
            r &&
                setInterval(async () => {
                    if (!(!r.installing && navigator)) return;

                    if ('connection' in navigator && !navigator.onLine) return;

                    const resp = await fetch(swUrl, {
                        cache: 'no-store',
                        headers: {
                            cache: 'no-store',
                            'cache-control': 'no-cache',
                        },
                    });
                    if (resp?.status === 200) await r.update();
                }, 60 * 1000);
        },
    });

    return <RouterProvider router={router} />;
};
